// ============================================================================
// FACILITATOR PROFILE PANEL — profile page card for certified facilitators:
// self-tag primary + secondary sectors, and view own training record.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Tags, Pencil, X, Loader, AlertCircle, GraduationCap } from 'lucide-react';
import {
  getMyFacilitatorProfile,
  updateMySectors,
  listSectors,
  MyFacilitatorProfile,
  Sector,
  SectorRef
} from '../../services/studio-bookings';
import { SectorList, TrainingStages, TrainingHistory } from './FacilitatorParts';

const MAX_SECONDARY = 10;

function SectorsDrawer({
  current,
  onClose,
  onSaved
}: {
  current: { primary: SectorRef | null; secondary: SectorRef[] };
  onClose: () => void;
  onSaved: (result: { primarySector: SectorRef | null; secondarySectors: SectorRef[] }) => void;
}) {
  const [catalogue, setCatalogue] = useState<Sector[]>([]);
  const [loading, setLoading] = useState(true);
  const [primary, setPrimary] = useState<string>(current.primary?.sectorId ?? '');
  const [secondary, setSecondary] = useState<string[]>(current.secondary.map(s => s.sectorId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listSectors()
      .then(setCatalogue)
      .catch(() => setError('Failed to load sectors'))
      .finally(() => setLoading(false));
  }, []);

  // Active catalogue plus any inactive sectors already tagged, so they can be kept or removed.
  const inactiveTagged = [current.primary, ...current.secondary]
    .filter((s): s is SectorRef => !!s && !s.isActive && !catalogue.some(c => c.sectorId === s.sectorId));
  const options: SectorRef[] = [
    ...catalogue.map(s => ({ sectorId: s.sectorId, name: s.name, isActive: true })),
    ...inactiveTagged
  ].sort((a, b) => a.name.localeCompare(b.name));

  function choosePrimary(id: string) {
    setPrimary(id);
    setSecondary(prev => prev.filter(s => s !== id));
  }

  function toggleSecondary(id: string) {
    setSecondary(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const result = await updateMySectors(primary || null, primary ? secondary : []);
      onSaved(result);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save sectors');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className="relative w-full max-w-md h-full bg-white shadow-xl border-l border-[#e2e8f0] flex flex-col">
        <header className="flex items-center justify-between p-5 border-b border-[#e2e8f0]">
          <div>
            <h2 className="font-bold text-[#052831] text-lg">Sectors I support</h2>
            <p className="text-sm text-slate-500">One primary sector, plus any others you can support</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          {loading ? (
            <div className="flex justify-center py-8"><Loader className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : (
            <>
              <label className="block">
                <span className="block text-sm font-semibold text-[#052831] mb-2">Primary sector</span>
                <select
                  value={primary}
                  onChange={e => choosePrimary(e.target.value)}
                  className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-[#052831]"
                >
                  <option value="">None</option>
                  {options.map(s => (
                    <option key={s.sectorId} value={s.sectorId} disabled={!s.isActive && s.sectorId !== current.primary?.sectorId}>
                      {s.name}{s.isActive ? '' : ' (inactive)'}
                    </option>
                  ))}
                </select>
              </label>

              <div>
                <p className="text-sm font-semibold text-[#052831] mb-1">Secondary sectors</p>
                <p className="text-xs text-slate-500 mb-3">
                  {primary ? `Up to ${MAX_SECONDARY}. ${secondary.length} selected.` : 'Choose a primary sector first.'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {options.filter(s => s.sectorId !== primary).map(s => {
                    const selected = secondary.includes(s.sectorId);
                    const blocked = !primary || (!selected && (!s.isActive || secondary.length >= MAX_SECONDARY));
                    return (
                      <button
                        key={s.sectorId}
                        type="button"
                        disabled={blocked}
                        onClick={() => toggleSecondary(s.sectorId)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                          selected
                            ? s.isActive ? 'bg-primary text-white border-primary' : 'bg-slate-200 text-slate-600 border-slate-300'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-primary/50'
                        }`}
                      >
                        {s.name}{s.isActive ? '' : ' (inactive)'}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        <footer className="p-5 border-t border-[#e2e8f0] flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 border border-[#e2e8f0] rounded-xl text-sm font-medium text-slate-500 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="flex-1 py-2.5 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {saving && <Loader className="w-4 h-4 animate-spin" />}
            Save sectors
          </button>
        </footer>
      </aside>
    </div>
  );
}

export function FacilitatorProfilePanel() {
  const [profile, setProfile] = useState<MyFacilitatorProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    getMyFacilitatorProfile()
      .then(setProfile)
      .catch(() => setError('Failed to load your facilitator profile'));
  }, []);

  const header = (icon: React.ReactNode, title: string, action?: React.ReactNode) => (
    <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">{icon}</div>
        <h3 className="font-bold text-[#052831] text-lg">{title}</h3>
      </div>
      {action}
    </div>
  );

  if (error) {
    return (
      <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm px-6 py-5 text-sm text-red-600">{error}</section>
    );
  }
  if (!profile) {
    return (
      <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm px-6 py-8 flex justify-center">
        <Loader className="w-5 h-5 animate-spin text-slate-400" />
      </section>
    );
  }

  return (
    <>
      <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden">
        {header(
          <Tags size={18} className="text-primary" />,
          'Sectors I support',
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-[#052831] font-bold px-4 py-2 rounded-xl transition-all text-sm"
          >
            <Pencil size={14} />
            Edit sectors
          </button>
        )}
        <div className="px-6 py-6">
          <SectorList primary={profile.primarySector} secondary={profile.secondarySectors} />
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden">
        {header(<GraduationCap size={18} className="text-primary" />, 'My facilitator training')}
        <div className="px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <TrainingStages training={profile.training} />
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">History</p>
            <TrainingHistory events={profile.events} />
          </div>
        </div>
      </section>

      {editing && (
        <SectorsDrawer
          current={{ primary: profile.primarySector, secondary: profile.secondarySectors }}
          onClose={() => setEditing(false)}
          onSaved={result => {
            setProfile(p => p && { ...p, ...result });
            setEditing(false);
          }}
        />
      )}
    </>
  );
}
